import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { http } from "../../lib/http";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";

const ROLES = ["admin", "receptionist", "teacher", "parent", "student"];

export default function UsersAdmin() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showRedacted, setShowRedacted] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      if (showRedacted) params.set("include_redacted", "true");
      params.set("limit", "50");
      const r = await http.get(`/admin/users?${params}`);
      setItems(r.data.items);
      setTotal(r.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [q, roleFilter, showRedacted]);

  return (
    <div data-testid="users-admin-page">
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        action={
          <Button onClick={() => setOpenCreate(true)} className="bg-[#C8102E] hover:bg-[#a50d25]" data-testid="add-user-button">
            + {t("users.addUser")}
          </Button>
        }
      />
      <PageBody>
        <div className="flex items-center gap-3 mb-4">
          <Input
            placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)}
            className="max-w-xs bg-white"
            data-testid="users-search-input"
          />
          <Select value={roleFilter || "all"} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-white" data-testid="users-role-filter">
              <SelectValue placeholder={t("users.role")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`users.roles.${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500">Total: {total}</div>
        </div>

        <div className="mb-3 flex items-center gap-2" data-testid="show-redacted-toggle">
          <Switch
            id="show-redacted"
            checked={showRedacted}
            onCheckedChange={setShowRedacted}
            data-testid="show-redacted-switch"
          />
          <Label htmlFor="show-redacted" className="text-sm text-slate-600 cursor-pointer">
            {t("users.showRedacted", "Show redacted users")}
          </Label>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">{t("users.name")}</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">{t("users.email")}</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">{t("users.role")}</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">{t("users.active")}</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">{t("users.lastLogin")}</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((u) => (
                <tr
                  key={u.id}
                  className={`hover:bg-slate-50/50 ${u.is_redacted ? "bg-slate-50/40 text-slate-400 italic" : ""}`}
                  data-testid={`user-row-${u.id}`}
                  data-redacted={u.is_redacted ? "true" : "false"}
                >
                  <td className="px-5 py-3 font-medium">
                    {u.is_redacted ? (
                      <div>
                        <div className="text-slate-500">[REDACTED]</div>
                        {u.redacted_at && (
                          <div className="text-xs text-slate-400 not-italic">
                            {t("users.redactedOn", "Redacted on")} {new Date(u.redacted_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[#0A1F44]">{u.full_name}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{u.is_redacted ? "—" : u.email}</td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className="font-normal capitalize" data-testid={`user-role-${u.id}`}>
                      {t(`users.roles.${u.role}`)}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block h-2 w-2 rounded-full mr-2 ${u.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                    {u.is_active ? t("common.yes") : t("common.no")}
                  </td>
                  <td className="px-5 py-3 text-slate-500 text-xs">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : t("users.never")}
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    {u.is_redacted ? (
                      <>
                        <span className="text-xs text-slate-400 mr-2">{t("users.redactedLabel", "Redacted — no actions")}</span>
                        {currentUser?.id !== u.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700 not-italic"
                            onClick={() => setDeleting(u)}
                            data-testid={`hard-delete-user-${u.id}`}
                          >
                            {t("users.hardDelete")}
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}>
                          {t("common.edit")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setResetting(u)} data-testid={`reset-pwd-${u.id}`}>
                          {t("users.resetPassword")}
                        </Button>
                        {currentUser?.id !== u.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleting(u)}
                            data-testid={`hard-delete-user-${u.id}`}
                          >
                            {t("users.hardDelete")}
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">{t("common.noResults")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>

      {openCreate && <CreateUserModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); fetchUsers(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchUsers(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => setResetting(null)} />}
      {deleting && (
        <HardDeleteUserModal
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); fetchUsers(); }}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", full_name: "", role: "student", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/admin/users", form);
      toast.success(t("users.userCreated"));
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="create-user-modal">
        <DialogHeader><DialogTitle>{t("users.createUser")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("users.name")}</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="create-user-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("users.email")}</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="create-user-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("users.role")}</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`users.roles.${r}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("login.password")}</Label>
            <Input type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="create-user-password" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={busy} className="bg-[#C8102E] hover:bg-[#a50d25]" data-testid="create-user-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ full_name: user.full_name, role: user.role, is_active: user.is_active });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.patch(`/admin/users/${user.id}`, form);
      toast.success(t("users.userUpdated"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="edit-user-modal">
        <DialogHeader><DialogTitle>{t("users.editUser")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("users.name")}</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="edit-user-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("users.role")}</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`users.roles.${r}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider">{t("users.active")}</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="edit-user-active" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={busy} className="bg-[#C8102E] hover:bg-[#a50d25]" data-testid="edit-user-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const { t } = useTranslation();
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post(`/admin/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success(t("users.passwordReset"));
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="reset-password-modal">
        <DialogHeader><DialogTitle>{t("users.resetPassword")} — {user.email}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">{t("users.newPassword")}</Label>
            <Input type="password" minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} required data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={busy} className="bg-[#C8102E] hover:bg-[#a50d25]" data-testid="reset-password-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HardDeleteUserModal({ user, onClose, onDeleted }) {
  const { t } = useTranslation();
  // user.email is the username; for redacted users it's stripped — fall back to id
  const username = user.email || user.id;
  const profileLabel = ({ teacher: "Teacher", parent: "Parent", student: "Student" })[user.role] || "—";

  const [step, setStep] = useState(1);   // 1 = warning, 2 = confirm form
  const [typed, setTyped] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const usernameMatches = typed === username;
  const canSubmit = usernameMatches && pwd.length > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const r = await http.delete(`/admin/users/${user.id}/hard-delete`, {
        data: { username_confirmation: typed, admin_password: pwd },
      });
      const c = r.data?.cascade || {};
      const bits = [];
      if (c.profile_deleted && c.profile_deleted !== "none") bits.push(`${c.profile_deleted} profile`);
      if (c.invoices_preserved) bits.push(`${c.invoices_preserved} invoices preserved`);
      if (c.attendance_preserved) bits.push(`${c.attendance_preserved} attendance preserved`);
      if (c.payments_preserved) bits.push(`${c.payments_preserved} SEPA batches`);
      const summary = bits.length ? bits.join(", ") : t("users.hardDelete");
      toast.success(t("users.hardDeleteSuccess", { summary }));
      onDeleted();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) setError(t("users.hardDeleteErrorPassword"));
      else if (status === 400 && /own account/i.test(detail || "")) setError(t("users.hardDeleteErrorSelf"));
      else if (status === 400) setError(t("users.hardDeleteErrorUsername"));
      else if (status === 403) {
        toast.error(t("users.hardDeleteErrorForbidden"));
        onClose();
      } else {
        setError(detail || t("users.hardDeleteErrorGeneric"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="hard-delete-user-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-red-600">{t("users.hardDeleteTitle")}</DialogTitle>
          <DialogDescription className="text-slate-600">
            {t("users.hardDeleteWarning")}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900 space-y-2">
              <div>
                <span className="font-semibold">{t("users.email")}:</span>{" "}
                <span data-testid="hard-delete-target-email">{username}</span>
              </div>
              <div>
                <span className="font-semibold">{t("users.role")}:</span>{" "}
                {t(`users.roles.${user.role}`)}
              </div>
              <div>
                <span className="font-semibold">{t("users.hardDeleteCascadeProfile")}:</span>{" "}
                {profileLabel}
              </div>
              <div className="text-xs text-red-700/80 pt-1">
                {t("users.hardDeleteCascadeNote")}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} data-testid="hard-delete-cancel-step1">
                {t("users.hardDeleteCancel")}
              </Button>
              <Button
                type="button"
                onClick={() => setStep(2)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="hard-delete-proceed"
              >
                {t("common.continue", "Continue")}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">
                {t("users.hardDeleteUsernamePrompt", { username })}
              </Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={t("users.hardDeleteUsernamePlaceholder")}
                autoComplete="off"
                data-testid="hard-delete-username-input"
              />
              {typed.length > 0 && !usernameMatches && (
                <p className="text-xs text-red-600 mt-1" data-testid="hard-delete-username-mismatch">
                  {t("users.hardDeleteErrorUsername")}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                {t("users.hardDeletePasswordPrompt")}
              </Label>
              <Input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoComplete="current-password"
                data-testid="hard-delete-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600" data-testid="hard-delete-inline-error">{error}</div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} data-testid="hard-delete-cancel-step2">
                {t("users.hardDeleteCancel")}
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                data-testid="hard-delete-confirm-button"
              >
                {busy ? t("common.loading") : t("users.hardDeleteConfirmButton")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
